import React, { useState } from 'react';
import { X } from 'lucide-react';

interface PermanentQRCodeModalProps {
  onClose: () => void;
  onCreate: (code: string, note: string) => Promise<void>;
}

const PermanentQRCodeModal: React.FC<PermanentQRCodeModalProps> = ({ onClose, onCreate }) => {
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError('Code is required');
      return;
    }

    if (!note.trim()) {
      setError('Note is required to remember what this code is used for');
      return;
    }

    try {
      setLoading(true);
      await onCreate(code.trim(), note.trim());
      onClose();
    } catch (err) {
      setError('Failed to create permanent QR code. Please try again.');
      console.error('Error creating permanent QR code:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Create Permanent QR Code</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permanent Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter the permanent code"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
              <p className="mt-2 text-sm text-gray-500">
                Enter the actual code that will be used for this permanent QR code
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note <span className="text-red-500">*</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter a note to remember what this code is used for"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={loading}
              />
              <p className="mt-2 text-sm text-gray-500">
                Add a description to remember at a later point what this code was used for
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">
                Permanent QR Code Features:
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Can be redeemed multiple times</li>
                <li>• Never expires</li>
                <li>• Tracks all redemption history</li>
                <li>• Cannot be bulk created</li>
                <li>• Can be deactivated or deleted</li>
                <li>• No hotel partner assignment (uses note instead)</li>
              </ul>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Permanent QR Code'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PermanentQRCodeModal;